import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail, API_BASE } from "@/lib/api";
import Layout from "@/components/Layout";
import UploadDialog from "@/components/UploadDialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Search, Trash2, Play, Music2, Loader2, Globe2, Lock } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import CoverArt from "@/components/CoverArt";
import { toast } from "sonner";

const EMPTY_BG =
  "https://images.pexels.com/photos/13129482/pexels-photo-13129482.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function CoverInitials({ song }) {
  return (
    <div className="aspect-square w-full relative">
      <CoverArt song={song} className="w-full h-full" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none rounded-sm" />
      <div className="absolute bottom-3 right-3 h-10 w-10 rounded-full bg-[#FF3B30] flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all pointer-events-none">
        <Play className="h-4 w-4 fill-current text-white ml-0.5" />
      </div>
    </div>
  );
}

export default function Library() {
  const [songs, setSongs] = useState(null);
  const [q, setQ] = useState("");

  const load = async (query = "") => {
    try {
      const { data } = await api.get("/songs", { params: query ? { q: query } : {} });
      setSongs(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load library");
      setSongs([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(q), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const remove = async (id) => {
    try {
      await api.delete(`/songs/${id}`);
      setSongs((s) => (s || []).filter((x) => x.id !== id));
      toast.success("Track removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const toggleVisibility = async (id, next) => {
    // Optimistic flip
    setSongs((s) => (s || []).map((x) => (x.id === id ? { ...x, is_public: next } : x)));
    try {
      const { data } = await api.patch(`/songs/${id}/visibility`, { is_public: next });
      setSongs((s) => (s || []).map((x) => (x.id === id ? { ...x, ...data } : x)));
      toast.success(next ? "Now public — visible on the feed" : "Set to private");
    } catch (e) {
      // Revert
      setSongs((s) => (s || []).map((x) => (x.id === id ? { ...x, is_public: !next } : x)));
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't update visibility");
    }
  };

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 pt-10 pb-32">
        {/* Hero header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B30]">
              // your library
            </div>
            <h1 className="mt-2 font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
              Every track. <br />
              <span className="text-white/30">Every word.</span>
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search title or artist"
                className="pl-9 w-64 bg-transparent border-white/10 focus-visible:border-[#FF3B30] focus-visible:ring-0 rounded-sm h-11"
                data-testid="library-search-input"
              />
            </div>
            <UploadDialog onUploaded={() => load(q)} />
          </div>
        </div>

        {/* Content */}
        {songs === null ? (
          <div className="py-32 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-white/40" />
          </div>
        ) : songs.length === 0 ? (
          <div
            className="relative border border-white/10 rounded-sm overflow-hidden"
            data-testid="library-empty"
          >
            <div
              className="absolute inset-0 opacity-30"
              style={{
                backgroundImage: `url(${EMPTY_BG})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/85 to-transparent" />
            <div className="relative px-8 py-20 max-w-2xl">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B30] mb-3">
                // empty library
              </div>
              <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[1]">
                Nothing in rotation yet.
              </h2>
              <p className="mt-4 text-white/60 max-w-md text-base">
                Upload an MP3 and bring the lyrics with you. The next time you hit play, every word will be right there.
              </p>
              <div className="mt-7">
                <UploadDialog onUploaded={() => load(q)} />
              </div>
            </div>
          </div>
        ) : (
          <div
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6"
            data-testid="library-grid"
          >
            {songs.map((s) => (
              <div
                key={s.id}
                className="group relative"
                data-testid="library-song-card"
              >
                <Link to={`/song/${s.id}`} className="block" data-testid={`song-link-${s.id}`}>
                  <CoverInitials song={s} />
                  <div className="mt-3 flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-display font-bold text-base truncate group-hover:text-white">
                        {s.title}
                      </div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mt-1 truncate">
                        {s.artist || "unknown"}
                      </div>
                    </div>
                    {s.is_synced && (
                      <span className="shrink-0 font-mono text-[9px] uppercase tracking-[0.25em] text-[#FF3B30] border border-[#FF3B30]/60 px-1.5 py-0.5 rounded-sm">
                        synced
                      </span>
                    )}
                  </div>
                </Link>

                {/* Public/Private toggle */}
                <div
                  className="mt-3 flex items-center justify-between gap-2 border border-white/5 bg-[#0F0F0F]/60 rounded-sm px-2.5 py-1.5"
                  data-testid={`visibility-row-${s.id}`}
                >
                  <div className="flex items-center gap-1.5 min-w-0">
                    {s.is_public ? (
                      <Globe2 className="h-3 w-3 text-[#FF3B30] shrink-0" />
                    ) : (
                      <Lock className="h-3 w-3 text-white/40 shrink-0" />
                    )}
                    <span
                      className={`font-mono text-[10px] uppercase tracking-[0.2em] truncate ${
                        s.is_public ? "text-[#FF3B30]" : "text-white/50"
                      }`}
                    >
                      {s.is_public ? "public" : "private"}
                    </span>
                  </div>
                  <Switch
                    checked={!!s.is_public}
                    onCheckedChange={(v) => toggleVisibility(s.id, v)}
                    className="data-[state=checked]:bg-[#FF3B30] data-[state=unchecked]:bg-white/10 h-4 w-7"
                    data-testid={`visibility-toggle-${s.id}`}
                  />
                </div>

                {/* Public corner badge on the cover */}
                {s.is_public && (
                  <span className="pointer-events-none absolute top-2 left-2 inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.25em] text-white bg-[#FF3B30] px-1.5 py-0.5 rounded-sm">
                    <Globe2 className="h-2.5 w-2.5" /> public
                  </span>
                )}

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute top-2 right-2 h-8 w-8 bg-black/60 hover:bg-[#FF3B30] hover:text-white text-white/80 opacity-0 group-hover:opacity-100 transition-opacity rounded-sm"
                      data-testid={`delete-song-btn-${s.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-[#0F0F0F] border-white/10 text-white rounded-sm">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="font-display font-black text-2xl tracking-tighter">
                        Delete this track?
                      </AlertDialogTitle>
                      <AlertDialogDescription className="text-white/60">
                        “{s.title}” will be removed from your library along with its lyrics. This can’t be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="bg-transparent border-white/10 text-white hover:bg-[#1A1A1A] hover:text-white rounded-sm">
                        Cancel
                      </AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => remove(s.id)}
                        className="bg-[#FF3B30] hover:bg-[#FF5C53] text-white rounded-sm"
                        data-testid={`confirm-delete-song-btn-${s.id}`}
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            ))}
          </div>
        )}

        {/* footer note */}
        <div className="mt-16 flex items-center gap-3 text-white/30">
          <Music2 className="h-4 w-4" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em]">
            // {songs?.length || 0} track{(songs?.length || 0) === 1 ? "" : "s"} in library
          </span>
        </div>
      </div>
    </Layout>
  );
}

// API_BASE imported in case future code needs it
void API_BASE;
